import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate, useOutletContext } from "react-router-dom";
import * as Icons from "lucide-react";
import { CATEGORIES, DIVISIONS } from "@/constants/site";
import { SUBCATEGORIES, CATEGORY_BROCHURE } from "@/constants/subcategories";
import api from "@/lib/api";
import { ArrowRight, ArrowLeft, FileDown, ShieldCheck, Package, Phone, MessageCircle } from "lucide-react";
import { useQuote } from "@/components/QuoteProvider";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
const PAGE_META = {
  "dye-penetrant-testing": {
    title: "Dye Penetrant Testing — MR Chemie DPT Materials Supplier | SSK Enterprises",
    desc: "MR Chemie dye penetrant testing materials distributed by SSK Enterprises across Telangana and Andhra Pradesh — cleaners, penetrants and developers for industrial inspection.",
    intro: "SSK Enterprises supplies MR Chemie dye penetrant testing materials for industrial inspection across Telangana and Andhra Pradesh. Our range covers the complete DPT workflow — pre-cleaning, penetrant, and developer — in visible and fluorescent formulations.",
  },
  "magnetic-particle-inspection": {
    title: "Magnetic Particle Inspection — MR Chemie MPI Materials | SSK Enterprises",
    desc: "MR Chemie magnetic particle inspection consumables — magnetic inks, powders, contrast paints and fluorescent magnetic particles supplied across Telangana and Andhra Pradesh.",
    intro: "SSK Enterprises supplies MR Chemie magnetic particle inspection materials for weld, casting and forging inspection on ferromagnetic components. The range supports wet and dry method inspection under visible and UV-A light.",
  },
  "ndt-equipment-accessories": {
    title: "NDT Equipment & Accessories — UV Lamps, Yokes, Meters | SSK Enterprises",
    desc: "NDT inspection equipment supplied by SSK Enterprises — UV lamps, hand yokes, lux meters, UV intensity meters, field indicators and inspection accessories.",
    intro: "SSK Enterprises supplies NDT inspection equipment and accessories to complete the MR Chemie material range. Category-wise supply of UV lamps, hand yokes, measuring instruments and workflow accessories, backed by product-selection assistance.",
  },
  "test-reference-blocks": {
    title: "Test & Reference Blocks — Penetrant & MPI Sensitivity Blocks | SSK Enterprises",
    desc: "Reference and comparator blocks for NDT process qualification — penetrant test panels, magnetic particle test blocks and customised test blocks supplied by SSK Enterprises.",
    intro: "Reference test blocks, comparator blocks and penetrant test panels for NDT process qualification and periodic sensitivity checks. Customised test blocks are manufactured to customer drawings for application-specific verification.",
  },
  "klyde-zinc-materials": {
    title: "KLYDE Zinc & Surface Protection Products | SSK Enterprises",
    desc: "KLYDE industrial surface protection and corrosion protection solutions — cold galvanizing, zinc sprays, zinc-rich primers, zinc paint and industrial protective coatings.",
    intro: "KLYDE zinc products are industrial surface-protection and corrosion-protection solutions for steel structures, fabrication units, maintenance workshops and OEM builds. SSK Enterprises supplies the complete KLYDE range across Telangana and Andhra Pradesh.",
  },
};

const LEGACY_REDIRECTS = {
  "ndt-materials": "dye-penetrant-testing",
  "ndt-equipment": "ndt-equipment-accessories",
};

export default function ProductsPage() {
  const { slug } = useParams();
  const legacyTarget = LEGACY_REDIRECTS[slug];
  const category = CATEGORIES[slug];
  const division = category ? DIVISIONS[category.division] : null;
  const subs = SUBCATEGORIES[slug] || [];
  const meta = PAGE_META[slug] || {};
  const [popular, setPopular] = useState(null);
  const { openQuote } = useQuote();
  const { settings } = useOutletContext() || { settings: {} };
  const brochure = CATEGORY_BROCHURE[slug];

  useSeo({
    title: meta.title,
    description: meta.desc,
    path: `/products/${slug}`,
    jsonLd: category ? [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Products", path: "/products" },
        { name: category.label, path: `/products/${slug}` },
      ]),
    ] : [],
  });

  useEffect(() => {
  if (!category) return;

  api.get(`/products?category_slug=${slug}`)
    .then((r) => {
      const data = r?.data ?? r;

      if (Array.isArray(data)) {
        setPopular(data);
      } else if (Array.isArray(data?.products)) {
        setPopular(data.products);
      } else {
        setPopular([]);
      }
    })
    .catch((error) => {
      console.error("Products API error:", error);
      setPopular([]);
    });
}, [slug, category]);
  
  if (legacyTarget) return <Navigate to={`/products/${legacyTarget}`} replace />;
  if (!category) return <Navigate to="/products" replace />;

  return (
    <>
      {/* 1. Professional Introduction */}
      <section className="bg-white border-b border-slate-200">
        <div className="container-x py-16 md:py-24">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0F2747] mb-6" data-testid="back-to-products">
            <ArrowLeft className="w-4 h-4" /> All Products
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div className="text-sm font-semibold text-[#0F2747] mb-3">{division.short}</div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-[#1E293B] leading-[1.05]">{category.label}</h1>
              <p className="text-slate-600 mt-6 text-lg leading-relaxed max-w-2xl">{meta.intro}</p>
            </div>
            <aside className="lg:col-span-4">
              <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl p-6">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-[#0F2747]" />
                  <div className="font-semibold text-[#1E293B]">Authorised Distributor</div>
                </div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT products.
                </p>
                <button onClick={() => openQuote({ division: category.division, product_name: category.label })} className="btn-accent w-full mt-5" data-testid="intro-request-quote">
                  Request a Quotation <ArrowRight className="w-4 h-4" />
                </button>
                {CATEGORY_CATALOGUE[slug] && (
                  <a
                    href={CATEGORY_CATALOGUE[slug].file}
                    download
                    className="btn-outline w-full mt-3 no-underline"
                    data-testid="intro-download-catalogue"
                  >
                    <FileDown className="w-4 h-4" /> Download Product Catalogue
                  </a>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* 2. Product Categories — clean 2-column icon list, no cards, no descriptions */}
      <section className="section section-alt" data-testid="subcategories-section">
        <div className="container-x">
          <div className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">Product Categories</h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3 max-w-4xl">
            {subs.map((s) => {
              const Ic = Icons[s.icon] || Icons.CircleDot;
              return (
                <li
                  key={s.label}
                  className="flex items-center gap-3 py-3 border-b border-slate-200/70"
                  data-testid={`subcategory-item-${s.label.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"")}`}
                >
                  <Ic className="w-4 h-4 text-[#B5121B] flex-shrink-0" strokeWidth={2.25} />
                  <span className="text-[#1E293B] font-semibold text-[15px]">{s.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
      {/* Zinc Showcase Image */}
{CATEGORY_SHOWCASE_ONLY.has(slug) && CATEGORY_HERO_IMAGE[slug] && (
  <section className="section">
    <div className="container-x">
      <div className="max-w-6xl mx-auto">
        <img
          src={CATEGORY_HERO_IMAGE[slug]}
          alt={CATEGORY_SHOWCASE_ALT[slug] || category.label}
          className="w-full h-auto object-contain rounded-xl"
        />
      </div>
    </div>
  </section>
)}
{/* 3. Popular Products */}
{!CATEGORY_SHOWCASE_ONLY.has(slug) && popular && popular.length > 0 && (
  <section className="section" data-testid="popular-products-section">
    <div className="container-x">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 max-w-3xl">
        <div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">
            Popular Products
          </h2>
          <p className="text-slate-600 mt-4">
            {CATEGORY_INTRO_COPY[slug] || `Browse our range of ${category.label}.`}
          </p>
        </div>
      </div>
{slug === "dye-penetrant-testing" ? (
  <>
    <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight mb-8 mt-10">
      Premium NDT Products
    </h2>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {PREMIUM_DPT_PRODUCTS.map((p) => (
        <PopularProductCard
          key={p.id}
          product={p}
          categorySlug={slug}
        />
      ))}
    </div>

    <div className="mt-14">
      <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight mb-8">
        Smart Choice Products
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SMART_CHOICE_PRODUCTS.map((p) => (
          <PopularProductCard
            key={p.id}
            product={p}
            categorySlug={slug}
          />
        ))}
      </div>
    </div>
  </>
) : (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {popular.map((p) => (
      <PopularProductCard
        key={p.id}
        product={p}
        categorySlug={slug}
      />
    ))}
  </div>
)}
    </div>
  </section>
)}
      {/* 4. Downloads — replaced by per-category catalogue button in the intro section */}
      {false && brochure && popular && popular.some((p) => p.tds_url || p.pdf_url) && (
        <section className="section section-alt" data-testid="downloads-section">
          <div className="container-x">
            <div className="max-w-3xl mb-8">
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E293B] leading-tight">Downloads</h2>
              <p className="text-slate-600 mt-4">Official manufacturer brochure for {category.label}.</p>
            </div>
            <a href={popular.find((p) => p.tds_url || p.pdf_url)?.tds_url || popular.find((p) => p.tds_url || p.pdf_url)?.pdf_url}
               target="_blank" rel="noreferrer"
               className="block max-w-2xl bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-[#0F2747] transition-all"
               data-testid="download-brochure">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 bg-[#0F2747] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileDown className="w-6 h-6" />
                </div>
                <div className="flex-grow">
                  <div className="font-display text-lg font-bold text-[#1E293B]">{brochure.label}</div>
                  <div className="text-sm text-slate-600 mt-1">Official manufacturer brochure — PDF</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#0F2747]">
                    Download PDF <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </a>
          </div>
        </section>
      )}

      {/* 5. Request a Quotation CTA */}
      <section className="section bg-[#0F2747] text-white" data-testid="page-cta">
        <div className="container-x grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-black leading-tight">Need a quotation or technical guidance?</h2>
            <p className="text-white/85 mt-4">Our team provides technical guidance, product selection assistance and prompt quotation support for your industrial requirements.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <button onClick={() => openQuote({ division: category.division, product_name: category.label })} className="btn-accent">
              Request a Quotation <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/contact" className="btn-ghost-light">Contact Us</Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* Popular Products card — image + code + name + Request Quote + WhatsApp + Brochure (no descriptions/apps/pack sizes) */
// Category-family fallback images for products supplied without a dedicated photo
const CATEGORY_CATALOGUE = {
  "dye-penetrant-testing": { file: "/brochures/dpt-brochure.pdf", label: "DPT Brochure" },
  "magnetic-particle-inspection": { file: "/brochures/mpi-brochure.pdf", label: "MPI Brochure" },
  "klyde-zinc-materials": { file: "/brochures/zincked-catalogue.pdf", label: "ZINCKED Catalogue — Feb 2026" },
};

const CATEGORY_HERO_IMAGE = {
  "klyde-zinc-materials": "/products/zincked-showcase.jpg",
  "magnetic-particle-inspection": "/products/mpi-materials-showcase.jpg",
};
const PREMIUM_DPT_PRODUCTS = [
  {
    id: "mr311-r",
    product_code: "MR311-R",
    name: "MR311-R Water Removable Red Penetrant",
    image_url: "/products/mr311-r.jpg",
    product_range: "premium",
    division: "ndt_materials",
  },
  {
    id: "mr62",
    product_code: "MR62",
    name: "MR62 Red Penetrant",
    image_url: "/products/mr62.jpg",
    product_range: "premium",
    division: "ndt_materials",
  },
  {
    id: "mr70i",
    product_code: "MR70I",
    name: "MR70I White Developer",
    image_url: "/products/mr70i.jpg",
    product_range: "premium",
    division: "ndt_materials",
  },
  {
    id: "mr85",
    product_code: "MR85",
    name: "MR85 Remover",
    image_url: "/products/mr85.jpg",
    product_range: "premium",
    division: "ndt_materials",
  },
];

// Categories that should render the hero image as the ENTIRE showcase (no product cards).
const CATEGORY_SHOWCASE_ONLY = new Set(["klyde-zinc-materials"]);
const CATEGORY_SHOWCASE_ALT = {
  "klyde-zinc-materials": "KLYDE ZINCKED product range — ZINCKED 50, 150, 550, 650, 1050, 1225 and 1525",
  "magnetic-particle-inspection": "MR Chemie MPI materials — MR76F fluorescent magnetic powder suspension, MR76S black magnetic powder suspension and MR72 white contrast paint",
};

const CATEGORY_SHOWCASE_COPY = {
  "klyde-zinc-materials": "Explore the KLYDE ZINCKED range of cold galvanizing sprays, zinc primers and brushable zinc coatings. Contact SSK Enterprises for product selection, availability and quotations.",
  "magnetic-particle-inspection": "MR Chemie Magnetic Particle Inspection materials — magnetic inks, powders, fluorescent and black suspensions, contrast paints and MPI consumables. Contact SSK Enterprises for product selection, availability and quotations.",
};

const CATEGORY_INTRO_COPY = {
  "dye-penetrant-testing": "Browse our range of genuine MR Chemie Dye Penetrant Testing materials. Contact us for product selection, technical assistance and quotations.",
  "magnetic-particle-inspection": "Browse our range of genuine MR Chemie Magnetic Particle Inspection materials. Contact us for product selection, technical assistance and quotations.",
  "klyde-zinc-materials": "Browse the KLYDE ZINCKED cold galvanizing and zinc protection range. Contact us for product selection, technical assistance and quotations.",
  "ndt-equipment-accessories": "Browse our range of NDT inspection equipment and accessories. Contact us for product selection, technical assistance and quotations.",
  "test-reference-blocks": "Browse our range of reference test blocks and comparator blocks. Contact us for product selection, technical assistance and quotations.",
};
const SMART_CHOICE_PRODUCTS = [
  {
    id: "sc20",
    product_code: "SC20",
    name: "SC20 Solvent Cleaner",
    image_url: "/products/sc20.jpg",
    product_range: "smart_choice",
    division: "ndt_materials",
  },
  {
    id: "sp10",
    product_code: "SP10",
    name: "SP10 Solvent Removable Penetrant",
    image_url: "/products/sp10.jpg",
    product_range: "smart_choice",
    division: "ndt_materials",
  },
  {
    id: "sd30",
    product_code: "SD30",
    name: "SD30 Solvent Developer",
    image_url: "/products/sd30.jpg",
    product_range: "smart_choice",
    division: "ndt_materials",
  },
];
function PopularProductCard({ product, categorySlug }) {
  const { openQuote } = useQuote();
  const { settings } = useOutletContext() || { settings: {} };
  const s = settings || {};
  const wa = (s.whatsapp || "").replace(/[^0-9]/g, "");
  const waMsg = encodeURIComponent(`Hello SSK Enterprises, I would like to enquire about "${product.name}".`);
  const waUrl = `https://wa.me/${wa}?text=${waMsg}`;
  const productImages = {
  "MR311-R Water Removable Red Penetrant": "/products/mr311-r.jpg",
  "MR62 Red Penetrant": "/products/mr62.jpg",
  "MR701 White Developer": "/products/mr701.jpg",
  "MR85 Remover": "/products/mr85.jpg",

  "SP10 Solvent Removable Penetrant": "/products/sp10.jpg",
  "SC20 Solvent Cleaner": "/products/sc20.jpg",
  "SD30 Solvent Developer": "/products/sd30.jpg",
};
const displayImg = productImages[product.name] || product.image_url || "";
  const isSmartChoice = product.product_range === "smart_choice";

  return (
    <div className="tech-card flex flex-col overflow-hidden" data-testid={`product-card-${product.id}`}>
      <div className={`aspect-[4/3] ${isSmartChoice ? "bg-white" : "bg-slate-100"} border-b border-slate-200 relative overflow-hidden`}>
        {displayImg ? (
          <img
            src={displayImg}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className={`w-full h-full ${isSmartChoice ? "object-contain object-center p-3" : "object-cover"}`}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
            <Package className="w-12 h-12" />
          </div>
        )}
        {!product.image_url && displayImg && (
          <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[9px] font-mono-tech uppercase tracking-wider px-2 py-0.5 rounded-sm">Family image</span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-grow">
        {product.product_code && (
          <div className="text-[11px] font-mono-tech tracking-[0.14em] uppercase text-[#0F2747] font-semibold">{product.product_code}</div>
        )}
        <h3 className="font-display font-bold text-[#1E293B] text-lg leading-snug mt-1">{product.name}</h3>       
      </div>
    </div>
  );
}
